// Modale drill-down d'un utilisateur pour l'admin.
// Ouvre depuis le tableau users, affiche : profil, compteurs, dernière séance,
// historique complet des abonnements, partenaires, groupes, rôles admin.
import React, { useEffect, useState } from "react";
import { adminUserDetail } from "../services/authService";

function formatDate(v) {
	if (!v) return "—";
	const d = new Date(v);
	if (isNaN(d)) return "—";
	return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

function formatDateTime(v) {
	if (!v) return "—";
	const d = typeof v === "number" ? new Date(v) : new Date(v);
	if (isNaN(d)) return "—";
	return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export default function AdminUserDetailModal({ userId, onClose }) {
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	useEffect(() => {
		(async () => {
			setLoading(true);
			const result = await adminUserDetail(userId);
			setLoading(false);
			if (result.success) setDetail(result.detail);
			else setError(result.error || "Erreur");
		})();
	}, [userId]);

	const p = detail?.profile;

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
				<div className="mens-modal-header">
					<h3>Fiche utilisateur</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				{loading && <div className="admin-loading">Chargement...</div>}
				{error && <div className="mens-modal-error">{error}</div>}

				{p && (
					<>
						{/* Profil */}
						<div className="user-detail-header">
							<div>
								<div className="user-detail-name">{p.prenom} {p.nom}</div>
								<div className="user-detail-email">{p.email}</div>
							</div>
							<div className="user-detail-badges">
								{p.emailConfirme
									? <span className="user-detail-badge success">Email confirmé</span>
									: <span className="user-detail-badge warn">Email non confirmé</span>}
								{p.hasGoogle && <span className="user-detail-badge">Google</span>}
								{p.hasApple && <span className="user-detail-badge">Apple</span>}
								{p.debugSync && <span className="user-detail-badge warn">Debug sync</span>}
								{detail.adminRoles?.map((r) => (
									<span key={r} className="user-detail-badge admin">{r}</span>
								))}
							</div>
						</div>

						<div className="user-detail-meta">
							<div><span>Inscription</span><strong>{formatDate(p.creationLog)}</strong></div>
							<div><span>Dernière synchro</span><strong>{formatDateTime(p.derniereSynchro)}</strong></div>
							<div><span>User ID</span><strong>#{p.id}</strong></div>
						</div>

						{/* Compteurs */}
						<div className="user-detail-counts">
							<div className="user-detail-count">
								<span className="value">{detail.counts?.sessions ?? 0}</span>
								<span className="label">Séances</span>
							</div>
							<div className="user-detail-count">
								<span className="value">{detail.counts?.templates ?? 0}</span>
								<span className="label">Modèles</span>
							</div>
							<div className="user-detail-count">
								<span className="value">{detail.counts?.exercises ?? 0}</span>
								<span className="label">Exercices</span>
							</div>
							<div className="user-detail-count">
								<span className="value">{detail.counts?.mensurations ?? 0}</span>
								<span className="label">Mensurations</span>
							</div>
							<div className="user-detail-count">
								<span className="value">{detail.counts?.pushTokens ?? 0}</span>
								<span className="label">Devices</span>
							</div>
						</div>

						{detail.lastSession && (
							<div className="user-detail-section">
								<h4>Dernière séance</h4>
								<div>{detail.lastSession.nom || "(sans titre)"} — {formatDateTime(detail.lastSession.date)}</div>
							</div>
						)}

						{/* Abonnements */}
						<div className="user-detail-section">
							<h4>Historique abonnements ({detail.subscriptions?.length ?? 0})</h4>
							{(!detail.subscriptions || detail.subscriptions.length === 0) ? (
								<p style={{ color: "var(--ink-soft)", margin: 0 }}>Aucun abonnement.</p>
							) : (
								<table className="user-detail-table">
									<thead>
										<tr>
											<th>Plan</th>
											<th>Période</th>
											<th>Statut</th>
											<th>Source</th>
										</tr>
									</thead>
									<tbody>
										{detail.subscriptions.map((s) => (
											<tr key={s.id}>
												<td>{s.planLibelle || "—"}</td>
												<td>
													{formatDate(s.dateDebut)}
													{s.dateFin && <> → {formatDate(s.dateFin)}</>}
												</td>
												<td>
													<span className={`user-detail-badge ${s.statut === "Actif" ? "success" : ""}`}>
														{s.statut}
													</span>
												</td>
												<td>
													<span className={`admin-source-tag source-${s.paymentSource || ""}`}>
														{s.paymentSource || "—"}
													</span>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>

						{/* Partenaires + Groupes en 2 colonnes */}
						<div className="user-detail-two-col">
							<div className="user-detail-section">
								<h4>Partenaires ({detail.partners?.length ?? 0})</h4>
								{(!detail.partners || detail.partners.length === 0) ? (
									<p style={{ color: "var(--ink-soft)", margin: 0 }}>Aucun partenaire.</p>
								) : (
									<ul className="user-detail-list">
										{detail.partners.map((p, i) => (
											<li key={i}>
												<span>{p.prenom} {p.nom}</span>
												<span className="user-detail-role">{p.role}</span>
											</li>
										))}
									</ul>
								)}
							</div>

							<div className="user-detail-section">
								<h4>Groupes ({detail.groups?.length ?? 0})</h4>
								{(!detail.groups || detail.groups.length === 0) ? (
									<p style={{ color: "var(--ink-soft)", margin: 0 }}>Aucun groupe.</p>
								) : (
									<ul className="user-detail-list">
										{detail.groups.map((g) => (
											<li key={g.id}>
												<span>{g.nom}</span>
												{g.isCreator && <span className="user-detail-role">Créateur</span>}
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					</>
				)}

				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose}>Fermer</button>
				</div>
			</div>
		</div>
	);
}
